import React from 'react';
import styled from '@emotion/styled';
import { DCard, ExternalLabel, FootNote, KvGrid, PrimaryButton, SecondaryButton } from './detailStyles';
import { DetailInfoModel } from './detailInfo';
import { ApplyActionState, actionBackground } from './ApplyAction';
import CouponFee, { PaidFee, PaidPrice } from './CouponFee';
import type { BestCouponPrice } from './useSeminarCoupons';

/*
 * PC 오른쪽 340px sticky 요약 카드(카드 안 18px).
 * 커리어: 일정·진행·모집·환불 → 참가비(26px) → 신청하기 52px → 공유하기 44px → 안내 2줄.
 * 일반: 일정·장소 → 참가비 + "외부 접수" → 안내 2줄. 버튼 없음.
 */

const SummaryBox = styled(DCard)`
  && {
    margin-top: 0;
  }
`;

const Divider = styled.div`
  border-top: 1px dashed #e5e7eb;
  margin: 14px 0 10px;
`;

const PriceCaption = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const CareerPrice = styled.div`
  margin-top: 2px;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.3;
  color: #111827;
  font-variant-numeric: tabular-nums;
`;

const GeneralPrice = styled.div`
  margin-top: 2px;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.3;
  color: #374151;
  font-variant-numeric: tabular-nums;
`;

const ApplyButton = styled(PrimaryButton)<{ actionMode: ApplyActionState['mode'] }>`
  height: 52px;
  margin-top: 14px;
  background: ${({ actionMode }) => actionBackground(actionMode)};

  &:disabled {
    background: ${({ actionMode }) => actionBackground(actionMode)};
  }
`;

const ShareButton = styled(SecondaryButton)`
  height: 44px;
  margin-top: 8px;
`;

/** 신청 버튼 바로 아래 한 줄 안내 */
const ActionHint = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #4b5563;
  text-align: center;
  word-break: keep-all;
`;

interface DetailSummaryCardProps {
  info: DetailInfoModel;
  action: ApplyActionState | null;
  onShare: () => void;
  notes: string[];
  /** 쿠폰 적용가 — 없으면 정가만 */
  couponPrice?: BestCouponPrice | null;
  /** 이미 결제했으면 결제한 금액 */
  paidPrice?: PaidPrice | null;
}

const DetailSummaryCard: React.FC<DetailSummaryCardProps> = ({ info, action, onShare, notes, couponPrice, paidPrice }) => (
  <SummaryBox aria-label="세미나 요약">
    <KvGrid labelWidth={64}>
      <dt>일정</dt>
      <dd>{info.scheduleText}</dd>
      {info.isCareer ? (
        <>
          {info.formatShort && (
            <>
              <dt>진행</dt>
              <dd>{info.formatShort}</dd>
            </>
          )}
          {info.recruitShort && (
            <>
              <dt>모집</dt>
              <dd>{info.recruitShort}</dd>
            </>
          )}
          {info.refundShort && (
            <>
              <dt>환불</dt>
              <dd>{info.refundShort}</dd>
            </>
          )}
          {info.provideText && (
            <>
              <dt>제공 기간</dt>
              <dd>{info.provideText}</dd>
            </>
          )}
        </>
      ) : (
        info.placeShort && (
          <>
            <dt>장소</dt>
            <dd>{info.placeShort}</dd>
          </>
        )
      )}
    </KvGrid>

    <Divider />

    {info.isCareer ? (
      <>
        <PriceCaption>{paidPrice ? '결제 금액' : couponPrice ? '쿠폰 적용가' : '참가비'}</PriceCaption>
        {paidPrice ? (
          <CareerPrice>
            <PaidFee paid={paidPrice} size="lg" />
          </CareerPrice>
        ) : couponPrice ? (
          <CareerPrice>
            <CouponFee best={couponPrice} listPrice={info.fee} size="lg" claimHint="쿠폰은 왼쪽에서 받을 수 있어요" />
          </CareerPrice>
        ) : (
          <CareerPrice>{info.feeText}</CareerPrice>
        )}
        {action && (
          <ApplyButton
            type="button"
            actionMode={action.mode}
            disabled={(action.mode === 'disabled' && !action.onClick) || action.busy}
            onClick={action.onClick}
          >
            {action.label}
          </ApplyButton>
        )}
        {action?.hint && <ActionHint>{action.hint}</ActionHint>}
        <ShareButton type="button" onClick={onShare}>공유하기</ShareButton>
      </>
    ) : (
      <>
        <PriceCaption>
          참가비<ExternalLabel>외부 접수</ExternalLabel>
        </PriceCaption>
        <GeneralPrice>{info.feeText}</GeneralPrice>
      </>
    )}

    {notes.length > 0 && (
      <FootNote>
        {notes.map((note: string) => (
          <div key={note}>· {note}</div>
        ))}
      </FootNote>
    )}
  </SummaryBox>
);

export default DetailSummaryCard;
